import {
  type DefaultError,
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useQueryScope } from "@/hooks/use-query-scope";
import type { QueryScope } from "@/queries/scope";

/**
 * Whether a domain hook's read can run yet.
 *
 * - `"cache"`: the session has said who is signed in, so the read goes through
 *   Query and `query` is live.
 * - `"waiting"`: the session has not resolved, or there is no workspace yet.
 *   Nothing is read: no key would be right.
 */
export type ScopedQueryMode = "cache" | "waiting";

export interface ScopedQuery<TData, TError = DefaultError> {
  readonly mode: ScopedQueryMode;
  /** Live in `"cache"` mode; otherwise a disabled query (pending, no data). */
  readonly query: UseQueryResult<TData, TError>;
}

export interface ScopedQueryOptions {
  /** `false` reads nothing, e.g. for a viewer who may not see it. */
  readonly enabled?: boolean;
}

/** Stands in for the principal while it is unknown; the query is disabled then. */
const NO_PRINCIPAL = "";

/**
 * The shared part of every domain hook (contract §9): the scope the read is
 * keyed under. A domain hook is then the query definition and its answer:
 *
 *     export function useMemberBank(slug: string) {
 *       const { workspaceService } = useStore();
 *       const { query } = useScopedQuery(slug, (scope) =>
 *         memberBankQuery(workspaceService, scope),
 *       );
 *       return query.data ?? [];
 *     }
 *
 * It is a real observer: an `invalidateQueries` from a writer, or an NG-2666
 * hint, re-reads what it shows. A hint for a hook-read domain is an
 * `observedRead` row in `stores/cache-hints/<domain>.ts`: `mark` invalidates
 * with `refetchType: "none"` and `reread` refetches the active, stale entries,
 * never `invalidateScoped`.
 *
 * `makeQuery` builds the definition from `app/queries`. It is called on every
 * render, also while the read waits (with a placeholder principal), so it must
 * be a pure options builder.
 *
 * Reads the signed-in user from a store, so the calling component must be an
 * `observer`.
 */
export function useScopedQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  tenant: string | undefined,
  makeQuery: (scope: QueryScope) => UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  { enabled = true }: ScopedQueryOptions = {},
): ScopedQuery<TData, TError> {
  const { mode, scope } = useScopedMode(tenant);
  const options = makeQuery(scope);
  const query = useQuery({
    ...options,
    enabled: mode === "cache" && enabled ? (options.enabled ?? true) : false,
  });

  return { mode, query };
}

/**
 * Whether the read can run, and the scope to build the query with: the
 * signed-in scope in `"cache"` mode, otherwise a placeholder whose query stays
 * disabled. Shared by `useScopedQuery` and `useScopedInfiniteQuery`.
 */
export function useScopedMode(tenant: string | undefined): {
  mode: ScopedQueryMode;
  scope: QueryScope;
} {
  const scope = useQueryScope(tenant);
  const mode: ScopedQueryMode = scope ? "cache" : "waiting";
  return { mode, scope: scope ?? { tenant: tenant ?? "", principal: NO_PRINCIPAL } };
}
